import React, { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, X } from 'lucide-react';
import './atom.css';

/**
 * Reusable "behind the screen" particle reveal.
 * Renders a recommendation (or any titled card) on top, with a collapsible
 * strip showing the named particles that produced it.
 */
export function ParticleReveal({
  headline, body, ctaLabel, onCta, onDismiss,
  particles = [], openByDefault = false, variant = 'atom',
  testid = 'atom-suggestion',
}) {
  const [open, setOpen] = useState(openByDefault);
  return (
    <div data-testid={testid} style={{
      background: '#FFFFFF',
      borderRadius: 20,
      boxShadow: '0 4px 20px rgba(176,49,64,0.10)',
      border: '1px solid rgba(176,49,64,0.08)',
      overflow: 'hidden',
      animation: 'gdlFadeIn 0.4s ease',
    }}>
      {/* Top: gradient header strip */}
      <div className="gdl-atom-grid" style={{
        background: 'linear-gradient(135deg, rgba(176,49,64,0.04), rgba(176,49,64,0.02))',
        padding: '14px 18px 10px',
        position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%',
            background: 'linear-gradient(135deg, #B03140, #8E2233)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(176,49,64,0.3)',
          }}>
            <Sparkles size={12} color="#fff" />
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                          color: '#8E2233', textTransform: 'uppercase' }}>
            Atom · {variant === 'atom' ? 'Recommendation' : variant}
          </div>
          {onDismiss && (
            <button onClick={onDismiss} data-testid={`${testid}-dismiss`}
              style={{ marginLeft: 'auto', background: 'transparent',
                        border: 'none', cursor: 'pointer',
                        color: '#A07078', padding: 4 }}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '14px 18px 18px' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10',
                       marginBottom: 6, lineHeight: 1.35 }}>
          {headline}
        </div>
        <div style={{ fontSize: 13, color: '#5A2A33', lineHeight: 1.55,
                       marginBottom: 14 }}>
          {body}
        </div>

        {ctaLabel && onCta && (
          <button onClick={onCta} data-testid={`${testid}-cta`} style={{
            width: '100%', padding: '12px 16px',
            background: '#B03140', color: '#fff',
            border: 'none', borderRadius: 12,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(176,49,64,0.25)',
            transition: 'transform 0.1s ease',
          }}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
            {ctaLabel}
          </button>
        )}

        {particles.length > 0 && (
          <>
            <button onClick={() => setOpen(o => !o)}
              data-testid={`${testid}-particles-toggle`}
              style={{ display: 'flex', alignItems: 'center', gap: 6,
                        marginTop: 14, padding: '6px 0',
                        background: 'transparent', border: 'none',
                        cursor: 'pointer', color: '#8E2233',
                        fontSize: 12, fontWeight: 600 }}>
              {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
              <span>{open ? 'Hide' : 'See'} how Atom drew this conclusion</span>
            </button>

            {open && (
              <div data-testid={`${testid}-particles`} style={{
                marginTop: 8, borderRadius: 12,
                background: 'rgba(176,49,64,0.04)',
                padding: '12px 14px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                {particles.map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    animation: `gdlParticleEnter 0.3s ease`,
                    animationDelay: `${i * 0.08}s`,
                    animationFillMode: 'backwards',
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: '#fff',
                      border: '1.5px solid #B03140',
                      flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, color: '#B03140',
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#2D0A10' }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#5A2A33', lineHeight: 1.4 }}>
                        {p.role}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Modal wrapper for action-triggered cross-sell suggestions. Uses the same
 * ParticleReveal underneath; adds a backdrop + "not now" dismiss.
 */
export function CrossSellModal({ suggestion, onAccept, onDismiss }) {
  if (!suggestion) return null;
  return (
    <div onClick={onDismiss} data-testid="crosssell-modal" style={{
      position: 'fixed', inset: 0,
      background: 'rgba(13,3,5,0.55)',
      animation: 'gdlBackdropIn 0.2s ease',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 50,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 390,
        background: 'transparent',
        padding: 16, paddingBottom: 28,
        animation: 'gdlModalIn 0.3s ease',
      }}>
        <ParticleReveal
          headline={suggestion.headline}
          body={suggestion.body}
          ctaLabel={suggestion.cta_label}
          onCta={() => onAccept(suggestion)}
          onDismiss={onDismiss}
          particles={suggestion.particles || []}
          testid="crosssell"
          openByDefault={false}
        />
        <button onClick={onDismiss} data-testid="crosssell-not-now" style={{
          marginTop: 10, width: '100%', padding: '12px 16px',
          background: 'transparent', color: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12,
          fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>
          Not now
        </button>
      </div>
    </div>
  );
}
